import { z } from 'zod';

/**
 * The shape of an `indskr_GetPricingBundle` payload as far as pricing depends on it. A bundle that
 * fails is not cached: a missing collection would read as everything deleted, and a number sent as
 * a string concatenates where it should add. Row content is not judged here, and any key not
 * named passes through, so a server adding columns never fails a sync.
 */
const id = z.string();
const value = z.number().nullish();
const date = z.string().nullish();
const flag = z.boolean().nullish();
const reference = z.string().nullish();

const item = z.looseObject({
  id,
  product: id,
  unitPrice: value,
  validFrom: date,
  validTo: date,
  minQuantity: value,
  maxQuantity: value,
  productCategory: reference,
  incrementQuantity: value,
  quotaPerAccount: value,
  isRecommended: flag,
});

const discountRule = z.looseObject({
  id,
  product: reference,
  role: value,
  offerGroup: value,
  percentage: value,
  amount: value,
  minQuantity: value,
  maxQuantity: value,
  thresholdCount: value,
  thresholdAmount: value,
  thresholdUsage: value,
  thresholdPercentage: value,
  thresholdPercentageUsage: value,
  evaluationLevel: value,
  unmapped: flag,
});

const freeGood = z.looseObject({
  id,
  product: reference,
  role: value,
  offerGroup: value,
  quantity: value,
  isRepeatable: flag,
  maxRewardQuantity: value,
  evaluationLevel: value,
  unmapped: flag,
});

const discountList = z.looseObject({
  id,
  type: value,
  valueType: value,
  offerShape: value,
  priority: value,
  isStackable: flag,
  validFrom: date,
  validTo: date,
  discountRules: z.array(discountRule),
  freeGoods: z.array(freeGood),
});

const priceList = z.looseObject({
  id,
  name: reference,
  currencyId: reference,
  currencyPrecision: value,
  minOrderValue: value,
  minOrderQuantity: value,
  minDeliveryValue: value,
  minDeliveryQuantity: value,
  minimumValueBasis: value,
  fetchXml: reference,
  priority: value,
  isDefault: flag,
  validFrom: date,
  validTo: date,
  items: z.array(item),
  discountLists: z.array(discountList),
});

export const PRICING_BUNDLE_SCHEMA = z.looseObject({
  changed: z.array(priceList),
  removed: z.array(z.looseObject({ id, reason: reference })).nullish(),
});
